import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  Avatar,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { RedditIcon } from '../../assets/icons/BrandIcons';
import api from '../../config/api';

const REDDIT_ORANGE = '#FF4500';

// Shown after Reddit OAuth: the signed-in Reddit member can usually see several ad accounts
// (across businesses). Each account the user ticks becomes its own connector.
const RedditAdsSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  const [accounts, setAccounts] = useState([]);
  const [username, setUsername] = useState('');
  const [tempId, setTempId] = useState(null);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    const temp_id = searchParams.get('temp_id');
    if (!temp_id) {
      setError('Invalid session. Please try authenticating again.');
      setLoading(false);
      return;
    }
    setTempId(temp_id);
    loadAccounts(temp_id);
  }, [searchParams]);

  const loadAccounts = async (temp_id) => {
    try {
      setLoading(true);
      const response = await api.get('/api/connectors/reddit-ads/accounts', { params: { temp_id } });
      if (response.data.success) {
        setAccounts(response.data.accounts || []);
        setUsername(response.data.reddit_username || '');
      } else {
        setError(response.data.message || 'Failed to load Reddit ad accounts');
      }
    } catch (err) {
      console.error('Error loading Reddit ad accounts:', err);
      setError(err.response?.data?.message || 'Failed to load Reddit ad accounts');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => {
    if (connecting) return;
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const handleConnect = async () => {
    if (selected.length === 0) {
      setError('Please choose at least one ad account to connect');
      return;
    }
    setConnecting(true);
    setError('');
    try {
      const response = await api.post('/api/connectors/reddit-ads/select', {
        temp_id: tempId,
        ad_account_ids: selected
      });
      if (response.data.success) {
        try {
          await api.post('/api/connectors/reddit-ads/cleanup', { temp_id: tempId });
        } catch (cleanupError) {
          console.warn('Failed to cleanup temp data:', cleanupError);
        }
        navigate('/connectors', {
          state: { message: response.data.message || 'Reddit Ads connected', severity: 'success' }
        });
      } else {
        // Partial success: keep the page so the failed accounts can be retried.
        const failedIds = (response.data.failures || []).map(f => f.ad_account_id);
        setSelected(failedIds);
        setError(response.data.message || 'Failed to connect the selected ad accounts');
      }
    } catch (err) {
      console.error('Error connecting Reddit ad accounts:', err);
      setError(err.response?.data?.message || 'Failed to connect the selected ad accounts');
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6">Loading Reddit ad accounts...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/connectors')} sx={{ mr: 2 }}>
          Back
        </Button>
        <Box sx={{ mr: 2, display: 'flex', flexShrink: 0 }}>
          <RedditIcon size={32} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Choose Reddit Ad Accounts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {username ? `Signed in as u/${username}. ` : ''}Each ad account you choose becomes its own connector.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select ad accounts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The connector syncs the account's campaigns, ad groups, ads and daily performance, in the account's own currency and time zone.
          </Typography>

          {accounts.length === 0 ? (!error && (
            <Alert severity="warning">
              No Reddit ad accounts found for this login. Ask a business admin to give you access to the ad account, then connect again.
            </Alert>
          )) : (
            <Grid container spacing={2}>
              {accounts.map((account) => {
                const isSelected = selected.includes(account.id);
                return (
                  <Grid item xs={12} md={6} key={account.id}>
                    <Card
                      variant="outlined"
                      onClick={() => toggle(account.id)}
                      sx={{
                        height: '100%',
                        cursor: connecting ? 'default' : 'pointer',
                        borderColor: isSelected ? REDDIT_ORANGE : undefined,
                        transition: 'all 0.2s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 }
                      }}
                    >
                      <CardContent>
                        <FormControlLabel
                          onClick={(e) => e.stopPropagation()}
                          control={
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggle(account.id)}
                              disabled={connecting}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#fff', border: '1px solid', borderColor: 'divider', mr: 2 }}>
                                  <RedditIcon size={22} />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {account.name || account.id}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {account.id}{account.business_name ? ` · ${account.business_name}` : ''}
                                  </Typography>
                                  {account.alreadyConnected && (
                                    <Box sx={{ mt: 0.5 }}>
                                      <Chip
                                        label={account.connectedBrandName
                                          ? `Connected · ${account.connectedBrandName}`
                                          : 'Already connected'}
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                              <Divider sx={{ my: 1 }} />
                              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                {account.currency && <Chip size="small" label={account.currency} />}
                                {account.time_zone_id && <Chip size="small" variant="outlined" label={account.time_zone_id} />}
                                {account.admin_approval && account.admin_approval !== 'VALID' && account.admin_approval !== 'TRUSTED' && (
                                  <Chip size="small" color="warning" label={account.admin_approval} />
                                )}
                              </Box>
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>

      {accounts.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/connectors')} disabled={connecting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={selected.length === 0 || connecting}
            startIcon={connecting ? <CircularProgress size={20} /> : <CheckIcon />}
            sx={{ backgroundColor: REDDIT_ORANGE, '&:hover': { backgroundColor: '#E03D00' } }}
          >
            {connecting ? 'Connecting...' : `Connect ${selected.length || ''} Account${selected.length === 1 ? '' : 's'}`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default RedditAdsSelection;
